<!--
  이 Vue 컴포넌트는 사용자의 회원가입 처리를 위한 폼 UI를 제공합니다.
  이름, 이메일, 비밀번호, 언어 선택 등 다양한 입력을 받고, 커스텀 드롭다운으로 언어를 선택할 수 있습니다.
  제출 시 API를 호출해 회원가입을 처리하며, 성공 시 로그인 페이지로 리디렉션됩니다.
  오류 메시지 표시, 로딩 상태 처리, 외부 클릭 시 드롭다운 닫기 기능도 포함되어 있습니다.
-->

<template>
  <div class="signup-page">
    <!-- 🔧 UI/UX 개선: 흰색 배경 적용 -->
    <AnimatedBackground backgroundColor="white" />
    
    <div class="signup-container">
      <h1 class="signup-title">Sign Up</h1>
      <form @submit.prevent="handleSignup" class="signup-form">
        <!-- 이름 입력 -->
        <div class="form-group">
          <label for="name">Name</label>
          <input type="text" id="name" v-model="name" required>
        </div>

        <!-- 이메일 입력 -->
        <div class="form-group">
          <label for="email">Email</label>
          <input type="email" id="email" v-model="email" required>
        </div>

        <!-- 비밀번호 입력 -->
        <div class="form-group">
          <label for="password">Password</label>
          <input type="password" id="password" v-model="password" required>
        </div>

        <!-- 비밀번호 확인 -->
        <div class="form-group">
          <label for="confirmPassword">Confirm Password</label>
          <input type="password" id="confirmPassword" v-model="confirmPassword" required>
        </div>

        <!-- 언어 선택 -->
        <div class="form-group">
          <label for="language">Language</label>
          <div class="custom-select" @click="toggleDropdown" ref="selectContainer">
            <div class="select-selected">
              <span>{{ getLanguageLabel(language) }}</span>
              <svg class="select-arrow" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M6 9l6 6 6-6"></path>
              </svg>
            </div>
            <div class="select-items" v-show="isDropdownOpen">
              <div
                v-for="option in languageOptions"
                :key="option.value"
                :class="['select-item', { 'selected': language === option.value }]"
                @click.stop="selectLanguage(option.value)"
              >
                <svg v-if="language === option.value" class="check-icon" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <polyline points="20 6 9 17 4 12"></polyline>
                </svg>
                <span>{{ option.label }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- 에러 메시지 출력 -->
        <p v-if="errorMessage" class="error-message">{{ errorMessage }}</p>

        <!-- 회원가입 버튼 -->
        <button type="submit" class="btn btn-primary" :disabled="isLoading">
          {{ isLoading ? 'Creating account...' : 'Sign Up' }}
        </button>
      </form>

      <p class="login-link">
        Already have an account?
        <router-link to="/login">Login</router-link>
      </p>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { useAuth } from '@/composables/useAuth'
import { languageOptions, getLanguageLabelByValue as getLanguageLabel } from '@/constants/languages'
// 🔧 UI/UX 개선: 애니메이션 배경 컴포넌트 import
import AnimatedBackground from '@/components/AnimatedBackground.vue'

const router = useRouter()
const { signup } = useAuth()

// 사용자 입력 상태
const name = ref('')
const email = ref('')
const password = ref('')
const confirmPassword = ref('')
const language = ref('한국어')
const isLoading = ref(false)
const errorMessage = ref('')

// 드롭다운 상태
const isDropdownOpen = ref(false)
const selectContainer = ref(null)

// 드롭다운 토글
const toggleDropdown = () => {
  isDropdownOpen.value = !isDropdownOpen.value
}

const selectLanguage = (value) => {
  language.value = value
  isDropdownOpen.value = false
}

const closeDropdownOnClickOutside = (event) => {
  if (selectContainer.value && !selectContainer.value.contains(event.target)) {
    isDropdownOpen.value = false
  }
}

onMounted(() => {
  document.addEventListener('click', closeDropdownOnClickOutside)
})

onUnmounted(() => {
  document.removeEventListener('click', closeDropdownOnClickOutside)
})

// 회원가입 처리 로직
const handleSignup = async () => {
  errorMessage.value = '';
  isLoading.value = true;
  const result = await signup({
      name: name.value, //name 추가
      email: email.value,
      password: password.value,
      language: language.value
  })
  if (result.success) {
    alert('회원가입에 성공했습니다.'); //회원가입 성공 시 알림 기능 추가 
    router.push('/login?registered=true');
  } else {
    errorMessage.value = result.message || '회원가입에 실패했습니다.'

  }
  isLoading.value = false;
}
</script>

<style scoped>
/* 🔧 UI/UX 개선: 애니메이션 배경이 보이도록 배경 제거 */
.signup-page {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  background: transparent;
  position: relative;
  overflow: hidden;
}

.signup-container {
  width: 100%;
  max-width: 400px;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 1rem;
  padding: 2rem;
  box-shadow: 0 20px 40px -10px rgba(0, 51, 160, 0.1),
              0 10px 20px -5px rgba(0, 0, 0, 0.05);
  position: relative;
  z-index: 1;
}

.signup-title {
  text-align: center;
  margin-bottom: 2rem;
  font-size: 2.5rem;
  font-weight: 700;
  background: linear-gradient(135deg, #0033a0, #8cb8e8, #0033a0);
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  animation: fadeInUp 1s ease-out;
}

.signup-form {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.form-group label {
  color: var(--foreground);
}

.form-group input {
  padding: 0.5rem;
  border: 1px solid var(--border);
  border-radius: var(--radius);
  background: var(--background);
  color: var(--foreground);
  width: 100%;
  height: 42px;
  font-size: 1rem;
}

/* 커스텀 드롭다운 스타일 추가 */
.custom-select {
  position: relative;
  width: 100%;
}

.select-selected {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem;
  border: 1px solid var(--border);
  border-radius: var(--radius);
  background: var(--background);
  color: var(--foreground);
  height: 42px;
  font-size: 1rem;
  cursor: pointer;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.select-selected:hover {
  border-color: var(--primary);
}

.select-arrow {
  transition: transform 0.2s;
}

.custom-select:has(.select-items:visible) .select-arrow {
  transform: rotate(180deg);
}

/* 드롭다운 스타일 - 흰색 배경, 검은색 텍스트로 수정 */
.select-items {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 99;
  max-height: 300px;
  overflow-y: auto;
  margin-top: 5px;
  border-radius: var(--radius);
  border: 1px solid var(--border);
  background: white; /* 배경색을 흰색으로 변경 */
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.2);
}

.select-item {
  padding: 10px;
  display: flex;
  align-items: center;
  cursor: pointer;
  color: #333; /* 텍스트 색상을 검은색으로 변경 */
}

.select-item:hover {
  background-color: rgba(0, 0, 0, 0.05); /* 호버 시 배경색을 밝은 회색으로 변경 */
}

.select-item.selected {
  background-color: rgba(0, 0, 0, 0.1); /* 선택된 항목 배경색을 약간 진한 회색으로 변경 */
}

.check-icon {
  margin-right: 8px;
  color: var(--primary); /* 체크 아이콘 색상은 유지 */
  min-width: 16px;
}

/* 스크롤바 스타일 업데이트 */
.select-items::-webkit-scrollbar {
  width: 8px;
}

.select-items::-webkit-scrollbar-track {
  background-color: rgba(255, 255, 255, 0.9);
}

.select-items::-webkit-scrollbar-thumb {
  background-color: var(--primary);
  border-radius: 4px;
}

/* Firefox용 스타일 */
.select-items {
  scrollbar-width: thin;
  scrollbar-color: var(--primary) rgba(0, 0, 0, 0.5);
}

.error-message {
  color: var(--destructive);
  font-size: 0.9rem;
  margin: 0;
  text-align: center;
}

.login-link {
  text-align: center;
  margin-top: 1.5rem;
  color: var(--muted-foreground);
}

.login-link a {
  color: var(--primary);
  text-decoration: none;
}

.login-link a:hover {
  text-decoration: underline;
}

/* 🔧 UI/UX 개선: 애니메이션 키프레임 추가 */
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>